/**
 * The visitor's last few actions before an error: which page they were on and
 * what they clicked. Attached to every client error report so the admin page
 * can say "they opened /dashboard, clicked Save listing, then it broke".
 *
 * Privacy: records the visible label of the thing clicked and the page path.
 * Never the contents of inputs, never query strings or hashes (they can carry
 * tokens), never anything from password or payment fields.
 *
 * Browser-only module state; safe to import from any client component.
 */
export type Breadcrumb = {
  /** ms since epoch */
  t: number
  kind: "click" | "nav"
  /** What was clicked, or the page navigated to. */
  label: string
  /** Page path at the time (no query string). */
  path: string
}

const MAX = 15
const buffer: Breadcrumb[] = []

function currentPath(): string {
  try { return window.location.pathname } catch { return "" }
}

export function addBreadcrumb(kind: Breadcrumb["kind"], label: string): void {
  const clean = label.replace(/\s+/g, " ").trim().slice(0, 80)
  if (!clean) return
  const last = buffer[buffer.length - 1]
  // Collapse repeats (double clicks, re-renders re-announcing the same page).
  if (last && last.kind === kind && last.label === clean && Date.now() - last.t < 1500) return
  buffer.push({ t: Date.now(), kind, label: clean, path: currentPath() })
  if (buffer.length > MAX) buffer.splice(0, buffer.length - MAX)
}

export function getBreadcrumbs(): Breadcrumb[] {
  return buffer.slice()
}

const SENSITIVE = /password|card|cvv|cvc|pin|otp|token|secret/i

/** A short human label for a clicked element, or null if not worth recording. */
export function labelFor(target: EventTarget | null): string | null {
  if (!(target instanceof Element)) return null
  const el = target.closest("button, a, [role=button], [role=tab], [role=link], summary, select, input[type=submit], input[type=button], input[type=checkbox], input[type=radio], label")
  if (!el) return null

  const name = (el.getAttribute("name") ?? "") + " " + (el.getAttribute("id") ?? "")
  if (SENSITIVE.test(name)) return null

  const tag = el.tagName.toLowerCase()
  const aria = el.getAttribute("aria-label")
  // For submit/button inputs the value IS the visible label; any other input's
  // value is user data and is deliberately never read.
  const inputLabel = tag === "input" && /^(submit|button)$/i.test((el as HTMLInputElement).type)
    ? (el as HTMLInputElement).value
    : ""
  const text = aria || inputLabel || (tag === "select" ? "" : (el as HTMLElement).innerText) || el.getAttribute("title") || ""
  const kind = tag === "a" ? "link" : tag === "select" ? "dropdown" : tag === "input" ? `${(el as HTMLInputElement).type}` : "button"

  let label = text.replace(/\s+/g, " ").trim().slice(0, 60)
  if (tag === "a") {
    const href = el.getAttribute("href") ?? ""
    const hrefPath = href.startsWith("/") ? href.split(/[?#]/)[0] : ""
    label = label ? `${label}${hrefPath ? ` (${hrefPath})` : ""}` : hrefPath
  }
  if (!label) label = (el.getAttribute("name") ?? "").slice(0, 40)
  return label ? `${kind}: ${label}` : null
}
